import axios from 'axios';
import { getGuestId } from '@/lib/services/guestId';
import { consumeLoginRedirect, loginRedirectUrl, sessionStore } from '@/lib/authRedirectGuard';
import { recoverFrom401, RETRY, REDIRECT } from '@/lib/sessionRenewal';
import { toast } from '@/lib/smartToast';
import { incognitoHeaders } from '@/lib/incognitoRequest';
import { announceQuotaExceeded } from '@/lib/usageQuota';
import { suggestUpgrade } from '@/lib/services/guest';

// Configure axios defaults for BFF cookie-based authentication
axios.defaults.withCredentials = true; // Send cookies with requests

const BFF_BASE_URL = process.env.VUE_APP_BFF_URL || 'https://localhost:44301'

axios.interceptors.request.use((config) => {
      // Add X-CSRF header required by BFF for anti-forgery protection
      config.headers['X-CSRF'] = '1';

      // Guest identity: always sent — the BFF only uses it when there is NO signed-in session,
      // deriving a namespaced userId from it so guests run the same server flows (capped).
      const guestId = getGuestId();
      if (guestId) config.headers['X-Guest-Id'] = guestId;

      // INCOGNITO (busy-my-web#407 / busy-main-bff#43, 2026-09-15 parity audit): while the mode is
      // on, ask / search / chat calls say so, and the BFF writes no story and no timeline row for
      // them. Additive and path-scoped — see lib/incognitoRequest.js — so a BFF without #43 simply
      // ignores a header it does not know, and every other call goes out exactly as before.
      Object.assign(config.headers, incognitoHeaders(config.url));
      
      // Optionally prefix API requests with BFF base URL if needed
      // This depends on your BFF configuration - uncomment if your API calls
      // should be routed through BFF
      // if (config.url && !config.url.startsWith('http')) {
      //   config.url = `${BFF_BASE_URL}${config.url}`;
      // }
      
      return config;
    },

    error => Promise.reject(error)
);

// Paths a guest may sit on without a session (mirrors the router's meta.public routes — the
// interceptor cannot import the router without creating an import cycle). On these pages a 401
// from a personal endpoint just means "guest": fail soft so components render their empty
// states, instead of bouncing the visitor to the IDS. Signing in is triggered deliberately at
// the save boundary (research store) — not by whichever background fetch happens to 401 first.
const PUBLIC_PATHS = [/^\/$/, /^\/home(\/|$)/, /^\/hub(\/|$)/];
const onPublicPage = () => PUBLIC_PATHS.some((p) => p.test(window.location.pathname));

/**
 * The app's own words for the one dead end left (busy-my-web#377): the session is over AND the
 * single login redirect has already been spent inside the window, so the page is drawn signed-out.
 * Before this the user was shown a logged-out page with no explanation at all.
 */
export const SESSION_ENDED_MESSAGE = 'Your session has ended. Sign in again to continue.'

/**
 * The session is gone: send the user to log in — ONCE (busy-my-web#321).
 *
 * The redirect itself is unchanged: the BFF's own `Location` hint where it sends one, otherwise
 * `/bff/login?returnUrl=<where we are>` — the full current URL, so the login returns the user to
 * exactly the route and query he was on. What #321 added is the refusal to do it twice in a
 * minute. When `/bff/login` completes silently (the IdP session is still valid) but the session it
 * hands back still cannot produce an access token, the next API call 401s and, before this guard,
 * the page bounced between Busy and the identity provider for as long as anything kept fetching.
 *
 * The second 401 inside the window is answered instead of obeyed: the user store is cleared so the
 * page draws its signed-out state, and the app says why in its own words (#377). The rule and its
 * state live in lib/authRedirectGuard.js — pure, and unit-testable without a browser.
 *
 * The store is pulled in dynamically on purpose: it imports the modules that import the service
 * layer that imports this file, and a static import would be an initialisation cycle at boot for
 * the sake of one dispatch on a path that almost never runs.
 */
const redirectToLogin = (hinted) => {
  if (consumeLoginRedirect(sessionStore())) {
    window.location.href = loginRedirectUrl({
      hinted,
      bffBaseUrl: BFF_BASE_URL,
      href: window.location.href,
    });
    return;
  }
  // Status only — never the body, and never the Location header (SAFETY: nothing about a session
  // goes into the console).
  console.warn('Auth redirect suppressed: a login redirect was already made for this 401');
  toast.info(SESSION_ENDED_MESSAGE);
  import('@/lib/store')
    .then((m) => m.default?.dispatch('user/fetchUser', null))
    .catch(() => { /* the page is already showing whatever it can */ });
};

/**
 * A 401 is checked before it is obeyed (busy-my-web#377).
 *
 * Both response arms land here. `/bff/user` is asked whether the session is actually over
 * (lib/sessionRenewal.js has the table): alive → the request is sent again once, silently, and
 * its result stands in for the 401; alive but the retry 401'd too → the failure is the caller's,
 * like any other; over → the one login redirect above; unknown → the failure is the caller's, a
 * redirect into an outage helps nobody (#26).
 *
 * auth.service is imported dynamically for the same cycle reason as the store: it imports the
 * store at module scope.
 *
 * @param {object} config the failed request's axios config
 * @param {string} hinted the BFF's Location hint, if any
 * @param {*} fallthrough what to hand back when the 401 is left to the caller — the response for
 *   the resolved arm, a rejected promise for the error arm
 */
const handleUnauthorized = async (config, hinted, fallthrough) => {
  const { auth } = await import('@/lib/services/auth.service');
  const { action, result } = await recoverFrom401({
    config,
    probe: () => auth.probeSession(),
    retry: (retried) => axios.request(retried),
  });
  if (action === RETRY) return result;
  if (action === REDIRECT) {
    redirectToLogin(hinted);
    return;
  }
  return fallthrough();
};

axios.interceptors.response.use(
  (response) => {
    if (response.status === 401) {
      if (onPublicPage()) return response;
      return handleUnauthorized(response.config, response.headers?.["location"], () => response);
    }

    return response;
  },
  (error) => {
    const status = error.response?.status;
    const location = error.response?.headers?.location;

    if (status === 401 || status === 302) {
      if (onPublicPage()) return Promise.reject(error);
      return handleUnauthorized(error.config, location, () => Promise.reject(error));
    }

    // The plan's monthly AI allowance is spent (busy-intelligent-srv#19, 429 `quota_exceeded`):
    // say so in plain words — and offer Pro to a Free user — before the caller's own handling.
    announceQuotaExceeded(error, { notify: (msg) => toast.error(msg), upgrade: suggestUpgrade });

    return Promise.reject(error);
  }
);
